import { deepClone } from "@mixitone/mvc/src/utils/deepClone";
import {
  type EntryDataField,
  type FormatSchema,
  type SchemaSettings,
  type StandingsData,
  type StandingsEntry,
  TournamentFormat,
} from "./TournamentFormat";
import {
  type Entry,
  type EntryId,
  type Match,
  type TournamentState,
  type Winner,
  blankTournamentState,
} from "./types";

const schema = {
  numberOfRounds: {
    type: "number",
    description: "Number of rounds to play (default: ceil(log2(numPlayers)))",
    minimum: 1,
  },
  pointsForWin: {
    type: "number",
    description: "Points awarded for a win",
    default: 1,
  },
  pointsForDraw: {
    type: "number",
    description: "Points awarded for a draw",
    default: 0.5,
  },
  pointsForLoss: {
    type: "number",
    description: "Points awarded for a loss",
    default: 0,
  },
} satisfies FormatSchema;

/**
 * Swiss system tournament + Monrad format implementation
 * Based on https://en.wikipedia.org/wiki/Swiss-system_tournament
 *
 * This format creates pairings where:
 * 1. Players with similar scores are paired together
 * 2. No player is eliminated, all players play in each round
 * 3. Players should not play against the same opponent more than once
 * 4. The winner is determined by total score after all rounds
 */
export class SwissFormat extends TournamentFormat<typeof schema> {
  getName(): string {
    return "Swiss/Monrad System";
  }

  get description(): string {
    return "Players continue playing matches against others with similar records";
  }

  getEntryLimits(): { min: number; max: number } {
    return {
      min: 2,
      max: 128,
    };
  }

  getSettingsSchema() {
    return schema;
  }

  getEntryDataSchema(): EntryDataField[] {
    return [
      {
        key: "score",
        label: "Score",
        type: "number" as const,
        description: "Total points earned",
        sortable: true,
      },
    ];
  }

  getMatchDataSchema(): EntryDataField[] {
    return [];
  }

  transform(state?: TournamentState, settings?: SchemaSettings<typeof schema>): TournamentState {
    // If no state provided, return empty state
    if (!state) {
      return blankTournamentState();
    }

    // Get entries from round 0
    const { entries } = state;

    // For 0 entries, return empty state
    if (entries.length === 0) {
      return blankTournamentState();
    }

    // Create a new tournament state starting with the entries
    const newState: TournamentState = deepClone(state);

    // For 1 entry, just return the original state
    if (entries.length === 1) {
      return newState;
    }

    // Calculate the default number of rounds if not specified in settings
    const defaultRounds = Math.ceil(Math.log2(entries.length));
    const numberOfRounds = settings?.numberOfRounds ?? defaultRounds;

    // Get point values from settings or defaults
    const pointsForWin = settings?.pointsForWin ?? schema.pointsForWin.default;
    const pointsForDraw = settings?.pointsForDraw ?? schema.pointsForDraw.default;
    const pointsForLoss = settings?.pointsForLoss ?? schema.pointsForLoss.default;

    // Initialize rounds array if needed
    if (!newState.rounds) newState.rounds = [];

    // Calculate current standings
    let standings = this.calculateStandings(newState, pointsForWin, pointsForDraw, pointsForLoss);

    // Get the current round index
    const currentRoundIndex = this.getCurrentRoundIndex(newState.rounds);

    // If we've already completed all rounds, just update the winners and return
    if (currentRoundIndex >= numberOfRounds) {
      // Ensure winners are set
      if (!newState.winners || newState.winners.length === 0) {
        newState.winners = this.determineWinners(standings, newState);
      }
      return newState;
    }

    // Make sure the rounds exist
    for (let i = 0; i < numberOfRounds; i++) {
      if (!newState.rounds[i]) {
        newState.rounds[i] = [];
      }

      if (this.isRoundComplete(newState.rounds[i])) {
        continue;
      }

      // Make sure each round has the correct number of matches
      for (let j = 0; j < entries.length / 2; j++) {
        if (!newState.rounds[i][j]) newState.rounds[i].push([null, null]);
      }

      // First, separate matches into fully populated (2 entries) and others
      const requiredMatches = Math.ceil(entries.length / 2);
      const matches = newState.rounds[i];
      const fullyPopulated = matches.filter((match) => match.filter((entry) => entry?.[0]).length === 2);
      const partialOrEmpty = matches.filter((match) => match.filter((entry) => entry?.[0]).length < 2);

      // Keep all fully populated matches plus enough partial/empty ones to meet minimum
      const neededExtra = Math.max(0, requiredMatches - fullyPopulated.length);
      newState.rounds[i] = [...fullyPopulated, ...partialOrEmpty.slice(0, neededExtra)];
    }

    // If current round is not complete, check if any matches have entries assigned
    if (!this.isRoundComplete(newState.rounds[currentRoundIndex])) {
      const currentRound = newState.rounds[currentRoundIndex] || [];

      // Check if any match in the round has entries assigned (players already set)
      // This preserves existing matches that were created in previous calls to transform
      // and prevents overwriting matches that already have winners declared
      const existingValidMatches = currentRound.filter((match) => match && (match[0]?.[0] || match[1]?.[0]));

      const entriesNotInRound = entries.filter((entry) => {
        // Check if the entry is not already in the current round
        return !currentRound.some((match) => {
          if (!match) return false;
          return match[0]?.[0] === entry[0] || match[1]?.[0] === entry[0];
        });
      });

      // No entries assigned yet, create new pairings
      const previousPairings = this.getPreviousPairings(newState.rounds.slice(0, currentRoundIndex));
      newState.rounds[currentRoundIndex] = [
        ...existingValidMatches,
        ...this.createPairings(entries, entriesNotInRound, standings, previousPairings),
      ];
    }

    standings = this.calculateStandings(newState, pointsForWin, pointsForDraw, pointsForLoss);

    // If all rounds are complete, determine the winners
    if (
      this.isRoundComplete(newState.rounds[currentRoundIndex]) &&
      currentRoundIndex === numberOfRounds - 1
    ) {
      newState.winners = this.determineWinners(standings, newState);
    }

    newState.entries = newState.entries.map((entry) => [
      entry[0],
      entry[1],
      {
        ...entry[2],
        score: standings.get(entry[0]),
      },
    ]);

    return newState;
  }

  /**
   * Calculate player standings (scores) based on match results
   */
  private calculateStandings(
    state: TournamentState,
    pointsForWin: number,
    pointsForDraw: number,
    pointsForLoss: number,
  ): Map<EntryId, number> {
    const standings = new Map<EntryId, number>();

    // Initialize all entries with 0 points
    state.entries.forEach((entry) => {
      standings.set(entry[0], 0);
    });

    // Process all completed rounds to calculate scores
    if (state.rounds) {
      state.rounds.forEach((round) => {
        if (!round) return;

        round.forEach((match) => {
          // Skip matches that haven't been set up
          if (!match) return;

          const player1 = match[0] ? match[0][0] : null;
          const player2 = match[1] ? match[1][0] : null;
          const player1Won = match[0] ? match[0][1] : false;
          const player2Won = match[1] ? match[1][1] : false;

          // Get current scores
          const score1 = player1 ? standings.get(player1) || 0 : 0;
          const score2 = player2 ? standings.get(player2) || 0 : 0;

          // Handle bye (player with no opponent)
          if (player1 && !player2) {
            // Player 1 got a bye
            if (player1Won) {
              standings.set(player1, score1 + pointsForWin);
            }
            return;
          }

          if (!player1 && player2) {
            // Player 2 got a bye
            if (player2Won) {
              standings.set(player2, score2 + pointsForWin);
            }
            return;
          }

          // Regular match
          if (player1 && player2) {
            if (player1Won && !player2Won) {
              // Player 1 won
              standings.set(player1, score1 + pointsForWin);
              standings.set(player2, score2 + pointsForLoss);
            } else if (!player1Won && player2Won) {
              // Player 2 won
              standings.set(player1, score1 + pointsForLoss);
              standings.set(player2, score2 + pointsForWin);
            } else if (player1Won && player2Won) {
              // Draw (both marked as winners)
              standings.set(player1, score1 + pointsForDraw);
              standings.set(player2, score2 + pointsForDraw);
            }
          }
        });
      });
    }

    return standings;
  }

  /**
   * Get the current round index (the next round to be played)
   */
  private getCurrentRoundIndex(rounds: Match[][]): number {
    // Find the first round that's incomplete
    for (let i = 0; i < rounds.length; i++) {
      const round = rounds[i];

      // If this round doesn't exist or has incomplete matches
      if (!round || round.length === 0 || !this.isRoundComplete(round)) {
        return i;
      }
    }

    // All rounds are complete, so return the next round index
    return rounds.length;
  }

  /**
   * Check if a round is complete (all matches have winners)
   */
  private isRoundComplete(round: Match[]): boolean {
    const matches = round.filter(Boolean);
    return matches.length > 0 && matches.every((match) => !match || match[0]?.[1] || match[1]?.[1]);
  }

  /**
   * Get all previous pairings to avoid repeated matchups
   */
  private getPreviousPairings(rounds: Match[][]): Set<string> {
    const pairings = new Set<string>();

    rounds.forEach((round) => {
      if (!round) return;

      round.forEach((match) => {
        if (match?.[0] && match[1]) {
          const player1 = match[0][0];
          const player2 = match[1][0];
          // Store both directions of the pairing
          pairings.add(`${player1}-${player2}`);
          pairings.add(`${player2}-${player1}`);
        }
      });
    });

    return pairings;
  }

  /**
   * Create pairings for a new round based on current standings
   */
  private createPairings(
    allEntries: Entry[],
    unpairedEntries: Entry[],
    standings: Map<EntryId, number>,
    previousPairings: Set<string>,
  ): Match[] {
    const standingSort = (a: Entry, b: Entry) => {
      const scoreA = standings.get(a[0]) || 0;
      const scoreB = standings.get(b[0]) || 0;

      if (scoreB !== scoreA) {
        return scoreB - scoreA; // Sort by score descending
      }

      return a[1] - b[1]; // Then by seed ascending
    };

    // Sort entries by score (descending) and then by seed (ascending)
    const sortedEntries = [...unpairedEntries].sort(standingSort);

    const pairings: Match[] = [];
    const entryIds = sortedEntries.map((entry) => entry[0]);
    const paired = new Set<EntryId>();
    const unpaired: EntryId[] = [];

    while (entryIds.length > 0) {
      const player1 = entryIds.shift()!;
      if (paired.has(player1)) continue;

      const player2 = entryIds.find((player) => {
        if (paired.has(player)) return false;
        const pairing = `${player1}-${player}`;
        return !previousPairings.has(pairing);
      });

      if (!player2) {
        // If no valid player2 found, add player1 to the unpaired list
        unpaired.push(player1);
        continue;
      }

      // Mark both players as paired
      paired.add(player1);
      paired.add(player2);
      pairings.push([
        [player1, false],
        [player2, false],
      ]);
    }

    // Handle unpaired players by creating more matches in the round
    if (unpaired.length > 0) {
      const allSorted = allEntries.toSorted(standingSort);

      unpaired.forEach((player) => {
        const player2 = allSorted.find(
          (entry) => entry[0] !== player && !previousPairings.has(`${player}-${entry[0]}`),
        );
        if (player2) {
          pairings.push([
            [player, false],
            [player2[0], false],
          ]);
        }
      });
    }

    return pairings;
  }

  /**
   * Determine winners based on final standings
   * If two entries have the same score, subsort by number of matches played
   * (entry with more matches comes above)
   */
  private determineWinners(standings: Map<EntryId, number>, state: TournamentState): Winner[] {
    // Count matches played for each entry
    const matchesPlayed = new Map<EntryId, number>();
    state.entries.forEach((entry) => {
      matchesPlayed.set(entry[0], 0);
    });

    if (state.rounds) {
      state.rounds.forEach((round) => {
        if (!round) return;
        round.forEach((match) => {
          if (!match) return;
          const player1 = match[0] ? match[0][0] : null;
          const player2 = match[1] ? match[1][0] : null;

          // Only count matches where players actually played (not byes)
          if (player1 && player2) {
            matchesPlayed.set(player1, (matchesPlayed.get(player1) || 0) + 1);
            matchesPlayed.set(player2, (matchesPlayed.get(player2) || 0) + 1);
          } else if (player1) {
            // Player 1 got a bye - still count as a match for them
            matchesPlayed.set(player1, (matchesPlayed.get(player1) || 0) + 1);
          } else if (player2) {
            // Player 2 got a bye - still count as a match for them
            matchesPlayed.set(player2, (matchesPlayed.get(player2) || 0) + 1);
          }
        });
      });
    }

    // Sort by score (descending), then by matches played (descending) when scores are equal
    const sortedStandings = Array.from(standings.entries()).sort(([idA, scoreA], [idB, scoreB]) => {
      if (scoreB !== scoreA) {
        return scoreB - scoreA; // Sort by score descending
      }
      // Scores are equal, subsort by matches played (descending)
      const matchesA = matchesPlayed.get(idA) || 0;
      const matchesB = matchesPlayed.get(idB) || 0;
      return matchesB - matchesA;
    });

    return sortedStandings.map(([entryId, score]) => [entryId, { score }]);
  }

  /**
   * Calculate detailed standings with played, wins, draws, losses, and points
   */
  private calculateDetailedStandings(
    state: TournamentState,
    pointsForWin: number,
    pointsForDraw: number,
    pointsForLoss: number,
  ): Map<EntryId, { points: number; played: number; wins: number; draws: number; losses: number }> {
    const standings = new Map<
      EntryId,
      { points: number; played: number; wins: number; draws: number; losses: number }
    >();

    // Initialize all entries with 0 stats
    state.entries.forEach((entry) => {
      standings.set(entry[0], {
        points: 0,
        played: 0,
        wins: 0,
        draws: 0,
        losses: 0,
      });
    });

    // Process all completed rounds to calculate stats
    if (state.rounds) {
      state.rounds.forEach((round) => {
        if (!round) return;

        round.forEach((match) => {
          // Skip matches that haven't been set up
          if (!match) return;

          const player1 = match[0] ? match[0][0] : null;
          const player2 = match[1] ? match[1][0] : null;
          const player1Won = match[0] ? match[0][1] : false;
          const player2Won = match[1] ? match[1][1] : false;

          // Get current standings
          const standing1 = player1 ? standings.get(player1) : null;
          const standing2 = player2 ? standings.get(player2) : null;

          // Skip if either player is not in standings
          if (!standing1 && !standing2) return;

          // Handle bye (player with no opponent)
          if (player1 && !player2 && standing1) {
            // Player 1 got a bye
            if (player1Won) {
              standing1.played += 1;
              standing1.wins += 1;
              standing1.points += pointsForWin;
              standings.set(player1, standing1);
            }
            return;
          }

          if (!player1 && player2 && standing2) {
            // Player 2 got a bye
            if (player2Won) {
              standing2.played += 1;
              standing2.wins += 1;
              standing2.points += pointsForWin;
              standings.set(player2, standing2);
            }
            return;
          }

          // Regular match - only count if match is completed
          if (player1 && player2 && standing1 && standing2) {
            // Only count completed matches
            if (!player1Won && !player2Won) return;

            // Update played count
            standing1.played += 1;
            standing2.played += 1;

            if (player1Won && !player2Won) {
              // Player 1 won
              standing1.wins += 1;
              standing1.points += pointsForWin;
              standing2.losses += 1;
              standing2.points += pointsForLoss;
            } else if (!player1Won && player2Won) {
              // Player 2 won
              standing1.losses += 1;
              standing1.points += pointsForLoss;
              standing2.wins += 1;
              standing2.points += pointsForWin;
            } else if (player1Won && player2Won) {
              // Draw (both marked as winners)
              standing1.draws += 1;
              standing1.points += pointsForDraw;
              standing2.draws += 1;
              standing2.points += pointsForDraw;
            }

            // Update standings
            standings.set(player1, standing1);
            standings.set(player2, standing2);
          }
        });
      });
    }

    return standings;
  }

  /**
   * Returns the standings data for the tournament
   * This calculates the current standings based on the tournament state
   *
   * @param state - The current tournament state
   * @param settings - Format-specific settings
   * @returns Standings data with a single "All" group
   */
  getStandings(state: TournamentState, settings?: SchemaSettings<typeof schema>): StandingsData {
    // Get settings with defaults
    const pointsForWin = settings?.pointsForWin ?? schema.pointsForWin.default;
    const pointsForDraw = settings?.pointsForDraw ?? schema.pointsForDraw.default;
    const pointsForLoss = settings?.pointsForLoss ?? schema.pointsForLoss.default;

    // Calculate detailed standings for all entries
    const standings = this.calculateDetailedStandings(state, pointsForWin, pointsForDraw, pointsForLoss);

    // Convert to StandingsData format with a single group
    const standingsData: StandingsData = {
      groups: {},
      settings: {
        pointsForWin,
        pointsForDraw,
        pointsForLoss,
      },
    };

    // Convert standings Map to StandingsEntry objects
    const entries: StandingsEntry[] = Array.from(standings.entries()).map(([entryId, stats]) => ({
      entryId,
      group: "All",
      points: stats.points,
      played: stats.played,
      wins: stats.wins,
      draws: stats.draws,
      losses: stats.losses,
    }));

    // Sort entries by points (descending), then by wins (descending), then by matches played (descending)
    const sortedEntries = entries.sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      if (b.wins !== a.wins) {
        return b.wins - a.wins;
      }
      return b.played - a.played;
    });

    // Add position numbers
    sortedEntries.forEach((entry, index) => {
      entry.position = index + 1;
    });

    standingsData.groups.All = sortedEntries;

    return standingsData;
  }

  hasStandings(): boolean {
    return true;
  }
}
